import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GoogleMapReact from "google-map-react";
import { ClipLoader } from "react-spinners";
import { MdLocationPin } from "react-icons/md";
import { getGoogleMapsApiKey, MapProvider } from "../utils/mapProvider";
import { loadYandexMaps } from "../utils/loadYandexMaps";

type Props = {
  lat: number;
  lng: number;
  provider: MapProvider;
  onChange: (lat: number, lng: number) => void;
  className?: string;
};

function PinMarker() {
  return (
    <div className="-translate-x-1/2 -translate-y-full">
      <MdLocationPin className="text-4xl text-[#FF3B30] drop-shadow" />
    </div>
  );
}

function GoogleInteractiveMap({
  lat,
  lng,
  onChange,
  className,
  badge = "Google Maps",
  hint = "Tap the map to move the pin",
}: {
  lat: number;
  lng: number;
  onChange: (lat: number, lng: number) => void;
  className: string;
  badge?: string;
  hint?: string;
}) {
  const googleKey = useMemo(() => getGoogleMapsApiKey(), []);

  const handleGoogleClick = useCallback(
    ({ lat: nextLat, lng: nextLng }: { lat: number; lng: number }) => {
      onChange(nextLat, nextLng);
    },
    [onChange],
  );

  return (
    <div className={`relative overflow-hidden bg-black/[0.04] ${className}`}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: googleKey }}
        center={{ lat, lng }}
        defaultZoom={15}
        options={{
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false,
        }}
        onClick={handleGoogleClick}
        yesIWantToUseGoogleMapApiInternals
      >
        {/* @ts-ignore google-map-react injects lat/lng onto child markers */}
        <PinMarker lat={lat} lng={lng} />
      </GoogleMapReact>
      <div className="pointer-events-none absolute left-3 top-3 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white backdrop-blur">
        {badge}
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
        <div className="rounded-full bg-black/55 px-3 py-1 text-[12px] text-white backdrop-blur">
          {hint}
        </div>
      </div>
    </div>
  );
}

function YandexInteractiveMap({
  lat,
  lng,
  onChange,
  className,
}: {
  lat: number;
  lng: number;
  onChange: (lat: number, lng: number) => void;
  className: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const placemarkRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  const [loading, setLoading] = useState(true);
  const [useGoogleFallback, setUseGoogleFallback] = useState(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let disposed = false;

    async function init() {
      if (!containerRef.current) return;

      try {
        setLoading(true);
        const ymaps = await loadYandexMaps("ru_RU");
        if (disposed || !containerRef.current) return;

        const map = new ymaps.Map(
          containerRef.current,
          {
            center: [lat, lng],
            zoom: 15,
            controls: ["zoomControl"],
          },
          {
            suppressMapOpenBlock: true,
          },
        );

        const placemark = new ymaps.Placemark(
          [lat, lng],
          {},
          {
            draggable: true,
            preset: "islands#redIcon",
          },
        );

        map.geoObjects.add(placemark);

        map.events.add("click", (event: any) => {
          const coords = event.get("coords") as [number, number];
          placemark.geometry.setCoordinates(coords);
          onChangeRef.current(coords[0], coords[1]);
        });

        placemark.events.add("dragend", () => {
          const coords = placemark.geometry.getCoordinates() as [
            number,
            number,
          ];
          onChangeRef.current(coords[0], coords[1]);
        });

        mapRef.current = map;
        placemarkRef.current = placemark;
        setLoading(false);

        window.setTimeout(() => {
          try {
            map.container.fitToViewport();
          } catch {
            // ignore
          }
        }, 120);
      } catch (e) {
        console.warn("Yandex Maps unavailable, falling back to Google Maps", e);
        if (!disposed) {
          setUseGoogleFallback(true);
          setLoading(false);
        }
      }
    }

    void init();

    return () => {
      disposed = true;
      if (mapRef.current) {
        try {
          mapRef.current.destroy();
        } catch {
          // ignore
        }
        mapRef.current = null;
        placemarkRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const placemark = placemarkRef.current;
    if (!map || !placemark) return;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const [currentLat, currentLng] = placemark.geometry.getCoordinates() as [
      number,
      number,
    ];
    if (
      Math.abs(currentLat - lat) < 0.000001 &&
      Math.abs(currentLng - lng) < 0.000001
    ) {
      return;
    }

    placemark.geometry.setCoordinates([lat, lng]);
    map.setCenter([lat, lng], map.getZoom(), { duration: 200 });
  }, [lat, lng]);

  if (useGoogleFallback) {
    return (
      <GoogleInteractiveMap
        lat={lat}
        lng={lng}
        onChange={onChange}
        className={className}
        badge="Google Maps"
        hint="Нажмите на карту, чтобы поставить метку"
      />
    );
  }

  return (
    <div className={`relative overflow-hidden bg-black/[0.04] ${className}`}>
      <div ref={containerRef} className="h-full w-full" />
      {loading && (
        <div className="absolute inset-0 grid place-content-center bg-secondary/70">
          <ClipLoader size={22} color="#007AFF" />
        </div>
      )}
      <div className="pointer-events-none absolute left-3 top-3 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white backdrop-blur">
        Yandex Maps
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
        <div className="rounded-full bg-black/55 px-3 py-1 text-[12px] text-white backdrop-blur">
          Нажмите на карту или перетащите метку
        </div>
      </div>
    </div>
  );
}

export default function InteractiveLocationMap({
  lat,
  lng,
  provider,
  onChange,
  className = "h-64 w-full",
}: Props) {
  const safeLat = Number.isFinite(lat) ? lat : 55.751244;
  const safeLng = Number.isFinite(lng) ? lng : 37.618423;

  if (provider === "yandex") {
    return (
      <YandexInteractiveMap
        lat={safeLat}
        lng={safeLng}
        onChange={onChange}
        className={className}
      />
    );
  }

  return (
    <GoogleInteractiveMap
      lat={safeLat}
      lng={safeLng}
      onChange={onChange}
      className={className}
    />
  );
}
